import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Badge, Modal } from 'react-bootstrap';
import dayjs from 'dayjs';
import MarketShare from 'components/partials/charts/ShareChart';
import FullPageSpinner from 'components/common/full-page-spinner';
import PerformanceTab from './PerformanceTab';
import { getProductionSCs, getSafetyStations } from 'services/productionOverviewService';

const AUTO_REFRESH_INTERVAL = 30 * 1000;
const SHIFTS = ['Night Shift', 'Day Shift', 'Late Shift'];

const safeArray = result => {
  const records = result?.data?.data || result?.data || [];
  return Array.isArray(records) ? records : [];
};

const buildShiftData = (allStations, completedChecks, shift) => {
  const shiftChecks = completedChecks.filter(c => c.shift === shift || c.Shift === shift);

  const completedStationIds = new Set(
    shiftChecks.map(c => c.stationId ?? c.StationId)
  );

  const completedStations = allStations.filter(s =>
    completedStationIds.has(s.stationId ?? s.StationId)
  );
  const notCompletedStations = allStations.filter(s =>
    !completedStationIds.has(s.stationId ?? s.StationId)
  );

  const peopleMap = {};
  shiftChecks.forEach(c => {
    const name = c.userName || c.UserName || `User ${c.userId || c.UserId}`;
    const station = c.stationName || c.StationName || '';
    const shift_ = c.shift || c.Shift || '';
    if (!peopleMap[name]) peopleMap[name] = { stations: new Set(), shift: shift_ };
    peopleMap[name].stations.add(station);
  });
  const people = Object.entries(peopleMap)
    .map(([name, { stations, shift: personShift }]) => ({
      name,
      shift: personShift,
      stations: [...stations],
      count: stations.size
    }))
    .sort((a, b) => b.count - a.count);

  return {
    shift,
    completed: completedStations.length,
    notCompleted: notCompletedStations.length,
    total: allStations.length,
    completedStations,
    notCompletedStations,
    people
  };
};

const pieColors = ['success', 'danger'];

const buildPieData = shiftData => [
  { name: 'Completed', value: shiftData.completed, color: pieColors[0] },
  { name: 'Not Completed', value: shiftData.notCompleted, color: pieColors[1] }
];

function ShiftRow({ shiftData, onClickPie }) {
  const pieData = buildPieData(shiftData);
  const hasData = shiftData.total > 0;

  return (
    <div className="border rounded bg-white p-3">
      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 20, alignItems: 'start' }}>
        <div style={{ cursor: 'pointer' }} onClick={() => onClickPie(shiftData)}>
          {hasData ? (
            <MarketShare
              data={pieData}
              radius={['95%', '83%']}
              title={`${shiftData.shift}`}
            />
          ) : (
            <div className="text-muted text-center p-4">No stations</div>
          )}
        </div>
        <div>
          <div className="fw-bold text-secondary mb-2" style={{ fontSize: 13 }}>
            Stations not completed ({shiftData.notCompleted})
          </div>
          {shiftData.notCompletedStations.length > 0 ? (
            <div className="d-flex flex-wrap gap-1">
              {shiftData.notCompletedStations.map(s => (
                <Badge
                  key={s.stationId ?? s.StationId}
                  bg="light"
                  text="dark"
                  className="border fw-normal"
                >
                  {s.stationName || s.StationName || `Station ${s.stationId ?? s.StationId}`}
                </Badge>
              ))}
            </div>
          ) : (
            <span className="text-success fw-semibold">All stations completed</span>
          )}
        </div>
      </div>
    </div>
  );
}

ShiftRow.propTypes = {
  shiftData: PropTypes.object.isRequired,
  onClickPie: PropTypes.func.isRequired
};

function CompletedModal({ detail, onHide }) {
  const show = Boolean(detail);

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {detail ? `${detail.shift} — Completed Checks` : 'Completed Checks'}
        </Modal.Title>
      </Modal.Header>
      {detail && (
        <Modal.Body>
          <div className="d-flex gap-3 align-items-center pb-3 border-bottom">
            <span className="fw-semibold text-success">
              {detail.completed} completed
            </span>
            <span className="fw-semibold text-danger">
              {detail.notCompleted} not completed
            </span>
            <span className="fw-semibold text-secondary">
              of {detail.total} stations
            </span>
          </div>

          {detail.people.length > 0 ? (
            <table className="table table-sm mt-3">
              <thead>
                <tr>
                  <th>Person</th>
                  <th>Shift</th>
                  <th>Checks</th>
                  <th>Stations</th>
                </tr>
              </thead>
              <tbody>
                {detail.people.map(p => (
                  <tr key={p.name}>
                    <td>{p.name}</td>
                    <td>{p.shift}</td>
                    <td>{p.count}</td>
                    <td>{p.stations.join(', ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center text-muted mt-3">No completed checks</div>
          )}
        </Modal.Body>
      )}
    </Modal>
  );
}

CompletedModal.propTypes = {
  detail: PropTypes.object,
  onHide: PropTypes.func.isRequired
};

export default function SafetyTab({ date }) {
  const [allStations, setAllStations] = useState([]);
  const [completedChecks, setCompletedChecks] = useState([]);
  const [modalDetail, setModalDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef(null);

  const loadChecks = useCallback((showLoading = false) => {
    if (showLoading) setLoading(true);
    return getProductionSCs(date)
      .then(result => setCompletedChecks(safeArray(result)))
      .catch(err => console.error('Error fetching safety checks', err))
      .finally(() => { if (showLoading) setLoading(false); });
  }, [date]);

  const loadStations = useCallback(() => {
    return getSafetyStations()
      .then(result => setAllStations(safeArray(result)))
      .catch(err => console.error('Error fetching safety stations', err));
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadStations(), getProductionSCs(date).then(result => setCompletedChecks(safeArray(result)))])
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [loadStations, date]);

  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);

    const isToday = dayjs(date, 'YYYY-MM-DD').isSame(new Date(), 'day');
    if (isToday) {
      timerRef.current = setInterval(() => loadChecks(false), AUTO_REFRESH_INTERVAL);
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [date, loadChecks]);

  const shiftDataList = useMemo(
    () => SHIFTS.map(shift => buildShiftData(allStations, completedChecks, shift)),
    [allStations, completedChecks]
  );

  return (
    <div style={{ position: 'relative' }}>
      <FullPageSpinner show={loading} quantity={3} />
      <h3 className="mb-3">Safety Performance</h3>
      <div className="d-flex flex-column gap-3">
        {shiftDataList.map(sd => (
          <ShiftRow
            key={sd.shift}
            shiftData={sd}
            onClickPie={setModalDetail}
          />
        ))}
      </div>
      <CompletedModal detail={modalDetail} onHide={() => setModalDetail(null)} />

      <div className="mt-4">
        <PerformanceTab
          title="Safety Checks Detail"
          data={completedChecks}
          detailPrefix="Operator Check"
          noDataMessage="No safety check data available for this date."
        />
      </div>
    </div>
  );
}

SafetyTab.propTypes = {
  date: PropTypes.string.isRequired
};
